import React, { useState } from 'react';
import ReactFlow, { Background, Controls, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { Modal, Button, Input, Select, message as antMessage } from 'antd';
import {
  LoadingOutlined,
  WarningOutlined,
  CheckOutlined,
  ReloadOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { botFlowApi } from './services/botFlowApi';
import BotNodeView from './BotNodeView';
import './AiFlowGenerator.css';

const { TextArea } = Input;

const NODE_TYPES = { bot: BotNodeView };

const LAYOUT_NODE_W = 260;
const LAYOUT_NODE_H = 130;

const PROMPT_CHIPS = [
  'Lead capture bot',
  'Customer support',
  'Appointment booking',
  'Product catalog',
  'FAQ bot',
];

// ── Dagre layout ─────────────────────────────────────────────────────────────
function applyDagreLayout(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 90, ranksep: 160, marginx: 50, marginy: 50 });
  g.setDefaultEdgeLabel(() => ({}));
  nodes.forEach((n) => g.setNode(n.id, { width: LAYOUT_NODE_W, height: LAYOUT_NODE_H }));
  edges.forEach((e) => {
    if (e.source && e.target) g.setEdge(e.source, e.target);
  });
  dagre.layout(g);
  return nodes.map((n) => {
    const p = g.node(n.id);
    if (!p) return n;
    return {
      ...n,
      position: { x: p.x - LAYOUT_NODE_W / 2, y: p.y - LAYOUT_NODE_H / 2 },
      targetPosition: 'left',
      sourcePosition: 'right',
    };
  });
}

// ── Edge visuals (mirror FlowBuilder) ────────────────────────────────────────
const getEdgeVisual = (condition) => {
  if (condition === 'success') {
    return {
      style: { stroke: '#16a34a', strokeWidth: 1.8 },
      labelStyle: { fontSize: 10, fill: '#15803d', fontWeight: 600 },
      animated: false,
    };
  }
  if (condition === 'error') {
    return {
      style: { stroke: '#dc2626', strokeWidth: 1.8 },
      labelStyle: { fontSize: 10, fill: '#b91c1c', fontWeight: 600 },
      animated: false,
    };
  }
  return {
    style: { stroke: '#4f46e5', strokeWidth: 1.5 },
    labelStyle: { fontSize: 10, fill: '#4338ca', fontWeight: 500 },
    animated: false,
  };
};

// ── Build ReactFlow graph from AI response ────────────────────────────────────
function buildReactFlowGraph(aiResult) {
  const { nodes: aiNodes = [], edges: aiEdges = [] } = aiResult;

  const nodeMap = {};
  aiNodes.forEach((n) => { nodeMap[n.tmp_id] = n; });

  const rfNodes = aiNodes.map((n) => ({
    id: n.tmp_id,
    type: 'bot',
    position: n.position || { x: 0, y: 0 },
    draggable: false,
    data: {
      node_type: n.node_type,
      is_start: n.is_start,
      content: n.content || {},
    },
  }));

  const rfEdges = aiEdges.map((e, i) => {
    let label;
    const cond = e.condition;
    if (cond === null || cond === undefined || cond === '') {
      label = undefined;
    } else if (cond === 'success') {
      label = 'Success';
    } else if (cond === 'error') {
      label = 'Error';
    } else {
      const src = nodeMap[e.from_tmp_id];
      const options = src?.content?.options || [];
      const opt = options.find((o) => o.id === cond);
      label = opt?.label || cond;
    }
    return {
      id: `aiedge_${i}_${e.from_tmp_id}_${e.to_tmp_id}`,
      source: e.from_tmp_id,
      target: e.to_tmp_id,
      sourceHandle: cond || 'default',
      ...(label !== undefined && { label }),
      ...getEdgeVisual(cond),
    };
  });

  const layoutedNodes = applyDagreLayout(rfNodes, rfEdges);
  return { rfNodes: layoutedNodes, rfEdges };
}

// ── Helper: unwrap axios response ─────────────────────────────────────────────
const unwrap = (res) => {
  const code = Number(res?.data?.statuscode ?? res?.data?.statusCode ?? 0);
  if (Number.isFinite(code) && code >= 400) {
    const err = new Error(res?.data?.message || 'Request failed');
    err.response = { data: res?.data };
    throw err;
  }
  return res.data?.data || res.data?.entity || res.data;
};

// ── Transform AI response → saveGraph payload ─────────────────────────────────
// AI endpoint returns { tmp_id, node_type, ... } and { from_tmp_id, to_tmp_id, ... }
// saveGraph expects { id, node_type, ... } and { from_node_id, to_node_id, ... }
const buildSavePayload = (aiResult) => {
  const nodes = (aiResult.nodes || []).map((n) => ({
    id: n.tmp_id,
    node_type: n.node_type,
    is_start: !!n.is_start,
    position: n.position || { x: 0, y: 0 },
    content: n.content || {},
  }));
  const edges = (aiResult.edges || []).map((e, i) => ({
    id: `e_${e.from_tmp_id}_${e.to_tmp_id}_${i}`,
    from_node_id: e.from_tmp_id,
    to_node_id: e.to_tmp_id,
    condition: e.condition ?? null,
  }));
  return { nodes, edges, flow: {} };
};

// ── Component ─────────────────────────────────────────────────────────────────
const AiFlowGenerator = ({ open, onClose }) => {
  const navigate = useNavigate();

  const [step, setStep] = useState('prompt_input'); // prompt_input | generating | preview | error
  const [prompt, setPrompt] = useState('');
  const [aiResult, setAiResult] = useState(null);
  const [rfNodes, setRfNodes] = useState([]);
  const [rfEdges, setRfEdges] = useState([]);
  const [genError, setGenError] = useState(null);
  const [newFlowName, setNewFlowName] = useState('');
  const [saveMode, setSaveMode] = useState(null); // null | 'new' | 'existing'
  const [existingFlows, setExistingFlows] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [warningsExpanded, setWarningsExpanded] = useState(false);
  const [confirmActiveOpen, setConfirmActiveOpen] = useState(false);
  const [pendingSaveFlowId, setPendingSaveFlowId] = useState(null);

  const resetState = () => {
    setStep('prompt_input');
    setPrompt('');
    setAiResult(null);
    setRfNodes([]);
    setRfEdges([]);
    setGenError(null);
    setSaveMode(null);
    setSaveError(null);
    setNewFlowName('');
    setSelectedFlowId(null);
    setExistingFlows([]);
    setWarningsExpanded(false);
  };

  const handleClose = () => {
    if (step === 'generating' || saving) return;
    onClose();
    setTimeout(resetState, 300);
  };

  // ── Generate ──────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    setStep('generating');
    setGenError(null);
    setSaveError(null);
    try {
      const res = await botFlowApi.generateFlow(prompt.trim());
      const data = res.data?.data || res.data?.entity || res.data;
      const { rfNodes: ln, rfEdges: le } = buildReactFlowGraph(data);
      setAiResult(data);
      setRfNodes(ln);
      setRfEdges(le);
      setNewFlowName(data?.meta?.suggestedName || 'AI Generated Flow');
      setSaveMode(null);
      setStep('preview');
    } catch (err) {
      const httpStatus = err?.response?.status || 0;
      let msg = err?.response?.data?.message || err.message || 'Something went wrong.';
      if (httpStatus === 502) {
        msg = "AI couldn't generate a valid flow. Try rephrasing your prompt.";
      } else if (httpStatus === 500) {
        msg = 'Something went wrong on the server. Please try again.';
      } else if (httpStatus === 400) {
        msg = `Invalid request: ${err?.response?.data?.message || msg}`;
      }
      setGenError({ message: msg, canRetry: true });
      setStep('error');
    }
  };

  const handleRegenerate = () => {
    setSaveMode(null);
    setSaveError(null);
    setAiResult(null);
    setRfNodes([]);
    setRfEdges([]);
    setStep('prompt_input');
  };

  // ── Load existing flows for dropdown ──────────────────────────────────────
  const loadExistingFlows = async () => {
    setLoadingFlows(true);
    try {
      const list = unwrap(await botFlowApi.listFlows());
      setExistingFlows(Array.isArray(list) ? list : []);
    } catch {
      setExistingFlows([]);
    } finally {
      setLoadingFlows(false);
    }
  };

  // ── Save as New Flow ──────────────────────────────────────────────────────
  const handleSaveAsNew = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const newFlow = unwrap(
        await botFlowApi.createFlow({ name: newFlowName.trim() || 'AI Generated Flow', trigger_type: 'always' })
      );
      const flowId = newFlow?.id ?? newFlow;
      await botFlowApi.saveGraph(flowId, buildSavePayload(aiResult));
      onClose();
      setTimeout(resetState, 300);
      navigate(`/dashboard/bot-flow/${flowId}`);
    } catch (err) {
      setSaveError(err?.response?.data?.message || err.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Save to Existing Flow ─────────────────────────────────────────────────
  const handleSaveToExisting = (flowId) => {
    const flow = existingFlows.find((f) => f.id === flowId);
    if (flow?.is_active) {
      setPendingSaveFlowId(flowId);
      setConfirmActiveOpen(true);
    } else {
      doSaveGraph(flowId);
    }
  };

  const doSaveGraph = async (flowId) => {
    setSaving(true);
    setSaveError(null);
    try {
      await botFlowApi.saveGraph(flowId, buildSavePayload(aiResult));
      onClose();
      setTimeout(resetState, 300);
      navigate(`/dashboard/bot-flow/${flowId}`);
    } catch (err) {
      setSaveError(err?.response?.data?.message || err.message || 'Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Render: Prompt Step ───────────────────────────────────────────────────
  const renderPromptStep = () => (
    <div className="aifg-prompt-step">
      <div className="aifg-prompt-header">
        <div className="aifg-prompt-icon">✨</div>
        <div>
          <h3 className="aifg-prompt-title">Generate a Bot Flow with AI</h3>
          <p className="aifg-prompt-sub">
            Describe your bot and AI will generate a complete node graph in seconds.
          </p>
        </div>
      </div>

      <TextArea
        rows={4}
        maxLength={2000}
        showCount
        placeholder="e.g. Customer support for an online store with order tracking, returns, FAQ, and live agent handoff"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        className="aifg-textarea"
      />

      <div className="aifg-chips">
        {PROMPT_CHIPS.map((chip) => (
          <button key={chip} type="button" className="aifg-chip" onClick={() => setPrompt(chip)}>
            {chip}
          </button>
        ))}
      </div>

      <p className="aifg-note">✦ AI will generate a preview you can review and edit before saving.</p>

      <div className="aifg-footer-actions">
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          type="primary"
          disabled={prompt.trim().length < 10}
          onClick={handleGenerate}
          className="aifg-generate-btn"
        >
          ✨ Generate Flow
        </Button>
      </div>
    </div>
  );

  // ── Render: Generating Step ───────────────────────────────────────────────
  const renderGeneratingStep = () => (
    <div className="aifg-generating">
      <LoadingOutlined className="aifg-spinner-icon" />
      <p className="aifg-gen-title">Generating your flow…</p>
      <p className="aifg-gen-sub">This usually takes 3–8 seconds.</p>
      <p className="aifg-gen-prompt-preview">
        "{prompt.length > 120 ? `${prompt.slice(0, 120)}…` : prompt}"
      </p>
    </div>
  );

  // ── Render: Error Step ────────────────────────────────────────────────────
  const renderErrorStep = () => (
    <div className="aifg-error-step">
      <ExclamationCircleOutlined className="aifg-error-icon" />
      <p className="aifg-error-msg">{genError?.message}</p>
      <div className="aifg-footer-actions">
        <Button onClick={handleClose}>Close</Button>
        {genError?.canRetry && (
          <Button type="primary" icon={<ReloadOutlined />} onClick={() => setStep('prompt_input')}>
            Try Again
          </Button>
        )}
      </div>
    </div>
  );

  // ── Render: Preview Step ──────────────────────────────────────────────────
  const renderPreviewStep = () => {
    if (!aiResult) return null;
    const { meta } = aiResult;
    const validationErrors = meta?.validationErrors || [];
    const selectedFlow = existingFlows.find((f) => f.id === selectedFlowId);

    return (
      <div className="aifg-preview-step">
        {/* Meta stats bar */}
        <div className="aifg-meta-bar">
          <span>{meta?.nodeCount ?? rfNodes.length} nodes</span>
          <span className="aifg-meta-sep">·</span>
          <span>{meta?.edgeCount ?? rfEdges.length} edges</span>
          {(meta?.provider || meta?.model) && (
            <>
              <span className="aifg-meta-sep">·</span>
              <span>
                Generated by{' '}
                <strong>{[meta.provider, meta.model].filter(Boolean).join('/')}</strong>
              </span>
            </>
          )}
          {meta?.responseTimeMs && (
            <>
              <span className="aifg-meta-sep">·</span>
              <span>{(meta.responseTimeMs / 1000).toFixed(1)}s</span>
            </>
          )}
        </div>

        {/* Validation warnings */}
        {validationErrors.length > 0 && (
          <div className="aifg-warn-banner">
            <span>
              <WarningOutlined /> AI generated this flow but it has{' '}
              {validationErrors.length} validation warning{validationErrors.length !== 1 ? 's' : ''}.
              Review carefully before saving.
            </span>
            <button
              type="button"
              className="aifg-warn-toggle"
              onClick={() => setWarningsExpanded((v) => !v)}
            >
              {warningsExpanded ? 'Hide' : 'Show'} details
            </button>
            {warningsExpanded && (
              <ul className="aifg-warn-list">
                {validationErrors.map((e, i) => (
                  <li key={i}>{e}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Canvas */}
        <div className="aifg-canvas-wrap">
          <ReactFlowProvider>
            <ReactFlow
              nodes={rfNodes}
              edges={rfEdges}
              nodeTypes={NODE_TYPES}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
              panOnDrag
              zoomOnScroll
              fitView
              fitViewOptions={{ padding: 0.18 }}
              proOptions={{ hideAttribution: true }}
            >
              <Background gap={16} color="#e5e7eb" />
              <Controls showInteractive={false} />
            </ReactFlow>
          </ReactFlowProvider>
        </div>

        {/* Save section */}
        <div className="aifg-save-section">
          {saveError && (
            <div className="aifg-save-error">
              <ExclamationCircleOutlined /> {saveError}
            </div>
          )}

          {/* Idle: show save choice buttons */}
          {!saveMode && (
            <div className="aifg-save-choices">
              <Button
                icon={<ReloadOutlined />}
                onClick={handleRegenerate}
                disabled={saving}
              >
                Regenerate
              </Button>
              <div className="aifg-save-choice-right">
                <Button
                  onClick={() => {
                    setSaveMode('existing');
                    loadExistingFlows();
                  }}
                  disabled={saving}
                >
                  Save to Existing Flow
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setSaveMode('new')}
                  disabled={saving}
                >
                  Save as New Flow
                </Button>
              </div>
            </div>
          )}

          {/* Save as New */}
          {saveMode === 'new' && (
            <div className="aifg-save-new">
              <label className="aifg-save-label">Flow Name</label>
              <div className="aifg-save-inline-row">
                <Input
                  value={newFlowName}
                  onChange={(e) => setNewFlowName(e.target.value)}
                  placeholder="Enter flow name"
                  onPressEnter={handleSaveAsNew}
                />
                <Button onClick={() => setSaveMode(null)} disabled={saving}>
                  Back
                </Button>
                <Button
                  type="primary"
                  icon={saving ? <LoadingOutlined /> : <CheckOutlined />}
                  onClick={handleSaveAsNew}
                  disabled={saving || !newFlowName.trim()}
                  loading={saving}
                >
                  Save Flow
                </Button>
              </div>
            </div>
          )}

          {/* Save to Existing */}
          {saveMode === 'existing' && (
            <div className="aifg-save-existing">
              <label className="aifg-save-label">Select a flow to replace its graph</label>
              <div className="aifg-save-inline-row">
                <Select
                  className="aifg-flow-select"
                  placeholder={loadingFlows ? 'Loading flows…' : 'Select flow…'}
                  loading={loadingFlows}
                  value={selectedFlowId}
                  onChange={(val) => setSelectedFlowId(val)}
                  options={existingFlows.map((f) => ({
                    value: f.id,
                    label: f.name + (f.is_active ? ' (ACTIVE)' : ''),
                  }))}
                />
                <Button
                  onClick={() => {
                    setSaveMode(null);
                    setSelectedFlowId(null);
                  }}
                  disabled={saving}
                >
                  Back
                </Button>
                <Button
                  type="primary"
                  danger
                  disabled={saving || !selectedFlowId}
                  loading={saving}
                  onClick={() => handleSaveToExisting(selectedFlowId)}
                >
                  Replace &amp; Save
                </Button>
              </div>
              {selectedFlow?.is_active && (
                <div className="aifg-active-warn">
                  ⚠️ This flow is currently <strong>active</strong>. Replacing it will take
                  effect immediately for all users.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Modal title ───────────────────────────────────────────────────────────
  const modalTitle = {
    prompt_input: '✨ Generate Bot Flow with AI',
    generating: '✨ Generating…',
    preview: '✨ AI Generated Flow — Preview',
    error: 'Generation Failed',
  }[step] ?? '✨ AI Flow Generator';

  return (
    <>
      <Modal
        open={open}
        onCancel={handleClose}
        closable={step !== 'generating' && !saving}
        maskClosable={step !== 'generating' && !saving}
        footer={null}
        title={modalTitle}
        width={step === 'preview' ? 980 : 620}
        className="aifg-modal"
        styles={{ body: { padding: 0 } }}
      >
        <div className="aifg-body">
          {step === 'prompt_input' && renderPromptStep()}
          {step === 'generating' && renderGeneratingStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'error' && renderErrorStep()}
        </div>
      </Modal>

      {/* Confirm overwrite active flow */}
      <Modal
        open={confirmActiveOpen}
        title="⚠️ Replace Active Flow"
        okText="Replace Anyway"
        okButtonProps={{ danger: true, loading: saving }}
        cancelText="Cancel"
        onCancel={() => {
          setConfirmActiveOpen(false);
          setPendingSaveFlowId(null);
        }}
        onOk={async () => {
          setConfirmActiveOpen(false);
          const fid = pendingSaveFlowId;
          setPendingSaveFlowId(null);
          await doSaveGraph(fid);
        }}
      >
        <p>
          This flow is currently <strong>active</strong>. Replacing it will take effect
          immediately for all users interacting with the bot.
        </p>
        <p>Are you sure you want to continue?</p>
      </Modal>
    </>
  );
};

export default AiFlowGenerator;
